/**
 * Word's ranges placed on a paragraph's accepted text: where each range Word cut at
 * the tokenizer's separators stands among the characters the engine's reader gives
 * for the paragraph once every tracked change is accepted.
 *
 * A quote, a comment's words or an edit is found in the accepted text, by the one
 * reader and the one occurrence finder every other caller uses. Word's own search
 * reads the paragraph with its tracked deletions still in it, so a quote with a
 * deletion between its words is never found there whole. Placed on the accepted
 * text, Word's ranges turn the stretch the reader found into the Word range that
 * stands on it, deleted words and all. The cell writer places Word's ranges in a cell
 * the same way before it writes the cell word by word (`CellChangeWriter`).
 *
 * EXAMPLE
 *   accepted text "… the Goods shall not exceed the lesser of: …"
 *   Word's ranges  "shall", "be", "limited", …, "($250,000)", "not", "exceed"
 *                  with accepted texts "shall", "", "", …, "", "not", "exceed"
 *   -> "shall" placed on its offsets, the struck words on none, "not" right after "shall "
 */
import { WordTokenizer, type WordToken } from "@common/utils/redline/text/word-tokenizer";

/**
 * One of Word's ranges cut at the tokenizer's separators, and the text Word reads in
 * it once every tracked change is accepted: "" for a range inside a tracked deletion.
 */
export interface WordRangeAcceptedReading {
    range: Word.Range;
    acceptedText: string;
}

/**
 * One of the diff's units a Word range holds: where it stands in the paragraph's accepted
 * text, and where in the range's own text as Word reads it, which is what a search of the
 * range looks through.
 */
export interface HeldDiffUnit {
    unitInText: WordToken;
    unitInRange: WordToken;
}

/**
 * A Word range placed on the characters of the paragraph's accepted text it stands on: a
 * stretch Word cut at the separators, or one of the diff's units a search cut out of such a
 * stretch (the cell writer's `rangesCutBySearches`).
 */
export interface WordRangeOnAcceptedText {
    range: Word.Range;
    /** Where the range starts in the accepted text. */
    startOffset: number;
    /** Where it ends, exclusive. */
    endOffset: number;
    /** What Word reads in the range once every change is accepted. */
    acceptedTextInWord: string;
    /** The diff's units the range holds, in order: one for a range a search cut out. */
    heldUnits: HeldDiffUnit[];
}

/** The Word range over a stretch of the accepted text, and whether it reaches past the stretch onto other words. */
export interface WordRangeOverStretch {
    range: Word.Range;
    /** True when the first or last range holds a word outside the stretch: the stretch starts or ends inside one of Word's ranges. */
    reachesPastTheStretch: boolean;
    /** The first of Word's ranges the stretch touches: where struck text Word glued before its first word sits. */
    firstRange: Word.Range;
    /** The last of Word's ranges the stretch touches, the first when it touches one: where struck text Word glued after its last word sits. */
    lastRange: Word.Range;
}

export class AcceptedTextWordRanges {
    /**
     * Each of Word's ranges that reads any word, placed on the accepted text it stands
     * on, in order.
     *
     * NULL
     *   Word's ranges do not read, unit for unit, as the accepted text (a field whose
     *   result Word reads differently, a hyperlink): no range can be trusted to stand
     *   where the reader's offsets say.
     *
     * WHY a range reaches over the separators Word left on it
     *   Word trims a space or a tab off the ranges it cuts, and leaves a non-breaking
     *   space on: "(30)" came back as the range "(30)" plus that space (live probe,
     *   2026-10-16). Placed over the characters the range really holds, the offsets of
     *   the units inside it stay where the reader has them.
     *
     * EXAMPLE
     *   accepted text "shall not exceed"     readings "shall", "", "not", "exceed"
     *   -> "shall" 0..5, "not" 6..9, "exceed" 10..16; the struck range is placed nowhere
     */
    static placedOn(acceptedText: string, readings: WordRangeAcceptedReading[]): WordRangeOnAcceptedText[] | null {
        const unitsInText = WordTokenizer.diffUnits(acceptedText);
        const unitsInEachRange = readings.map((reading) => WordTokenizer.diffUnits(reading.acceptedText));
        const unitKeysInRanges = unitsInEachRange.flat().map((unitInRange) => WordTokenizer.comparisonKey(unitInRange.text));
        const unitKeysInText = unitsInText.map((unitInText) => WordTokenizer.comparisonKey(unitInText.text));
        /**
         * WHEN Word's ranges read other words than the accepted text
         *   CONSEQUENCE
         *     null: the ranges cannot be placed on it.
         */
        if (unitKeysInRanges.join(" ") !== unitKeysInText.join(" ")) return null;
        const placedRanges: WordRangeOnAcceptedText[] = [];
        let nextUnitInTextIndex = 0;
        readings.forEach((reading, readingIndex) => {
            const unitsInRange = unitsInEachRange[readingIndex];
            /**
             * WHEN Word reads no unit in the range: it sits inside a tracked deletion
             *   CONSEQUENCE
             *     it stands on no character of the accepted text and is placed nowhere
             */
            if (unitsInRange.length === 0) return;
            const placedRange = AcceptedTextWordRanges.rangePlacedOver(acceptedText, reading, unitsInRange, unitsInText, nextUnitInTextIndex);
            nextUnitInTextIndex += unitsInRange.length;
            placedRanges.push(placedRange);
        });
        return placedRanges;
    }

    /**
     * The Word range that stands on the stretch `startOffset`..`endOffset` of the
     * accepted text, widened to the whole of every range it touches, and whether that
     * widening reached onto words outside the stretch.
     *
     * CASES
     *   - the stretch starts inside a range -> from that range's start
     *   - it ends inside a range            -> to that range's end
     *   - it touches one range              -> that range
     *
     * WHY say whether it reaches past the stretch
     *   Japanese, Chinese and Thai put no spaces between words, so Word cuts a sentence
     *   of them as one range; a quote of part of it is widened to the whole sentence,
     *   which a caller may narrow by another means.
     *
     * NULL
     *   No placed range touches the stretch.
     *
     * EXAMPLE
     *   placed "shall" 0..5, "not" 6..9, "exceed" 10..16; stretch 6..16 -> "not" expanded to "exceed",
     *   reaching past nothing
     *   placed "本契約は、…ものとする。" 0..36 as one range; stretch 5..20 -> that range, reaching past the stretch
     */
    static spanOver(acceptedText: string, placedRanges: WordRangeOnAcceptedText[], startOffset: number, endOffset: number): WordRangeOverStretch | null {
        const touchingRanges = placedRanges.filter((placedRange) => placedRange.endOffset > startOffset && placedRange.startOffset < endOffset);
        /**
         * WHEN no placed range touches the stretch
         *   CONSEQUENCE
         *     null: nothing in Word stands on it.
         */
        if (touchingRanges.length === 0) return null;
        const firstTouchingRange = touchingRanges[0];
        /**
         * EXAMPLE
         *   3 touching ranges -> the last is index 3 - 1 = 2
         */
        const lastTouchingRange = touchingRanges[touchingRanges.length - 1];
        /**
         * The words the touching ranges hold before the stretch starts and after it ends,
         * read by the tokenizer: none when the stretch starts and ends on range edges.
         *
         * EXAMPLE
         *   first range 0..36, stretch 5..20 -> units in 0..5 and in 20..36 -> reaches past
         */
        const wordsBeforeTheStretch = WordTokenizer.diffUnits(acceptedText.slice(firstTouchingRange.startOffset, startOffset));
        const wordsAfterTheStretch = WordTokenizer.diffUnits(acceptedText.slice(endOffset, lastTouchingRange.endOffset));
        /**
         * CASES
         *   - one range touches the stretch -> that range
         *   - several do                    -> the first expanded to the last
         */
        const range = firstTouchingRange === lastTouchingRange ? firstTouchingRange.range : firstTouchingRange.range.expandTo(lastTouchingRange.range);
        return {
            range,
            reachesPastTheStretch: wordsBeforeTheStretch.length > 0 || wordsAfterTheStretch.length > 0,
            firstRange: firstTouchingRange.range,
            lastRange: lastTouchingRange.range,
        };
    }

    /**
     * One reading's range placed over the units of the accepted text it holds, which
     * start at `firstUnitInTextIndex`.
     *
     * EXAMPLE
     *   reading "(30)" plus a non-breaking space, its one unit "(30)" at 0..4 of the reading,
     *   and unit 7 of the text at 30..34
     *   -> separators before 0, after 5 - 4 = 1 -> placed 30..35
     */
    private static rangePlacedOver(
        acceptedText: string,
        reading: WordRangeAcceptedReading,
        unitsInRange: WordToken[],
        unitsInText: WordToken[],
        firstUnitInTextIndex: number,
    ): WordRangeOnAcceptedText {
        const firstUnitInText = unitsInText[firstUnitInTextIndex];
        /**
         * EXAMPLE
         *   first unit index 7, the range holds 3 units -> the last is unit 7 + 3 - 1 = 9 of the text
         */
        const lastUnitInText = unitsInText[firstUnitInTextIndex + unitsInRange.length - 1];
        /**
         * EXAMPLE
         *   3 units in the range -> its last is index 3 - 1 = 2
         */
        const lastUnitInRange = unitsInRange[unitsInRange.length - 1];
        const separatorsBeforeFirstUnit = unitsInRange[0].startOffset;
        const separatorsAfterLastUnit = reading.acceptedText.length - lastUnitInRange.endOffset;
        return {
            range: reading.range,
            acceptedTextInWord: reading.acceptedText,
            /**
             * EXAMPLE
             *   first unit index 7, the range's second unit (offset 1) -> unit 7 + 1 = 8 of the text
             */
            heldUnits: unitsInRange.map((unitInRange, unitOffsetInRange) => ({ unitInText: unitsInText[firstUnitInTextIndex + unitOffsetInRange], unitInRange })),
            /**
             * EXAMPLE
             *   unit at 30 of the text, 0 separators before it in the range -> 30; never below 0
             */
            startOffset: Math.max(firstUnitInText.startOffset - separatorsBeforeFirstUnit, 0),
            /**
             * EXAMPLE
             *   unit ends at 34 of the text, 1 separator after it in the range -> 35; never past the text's end
             */
            endOffset: Math.min(lastUnitInText.endOffset + separatorsAfterLastUnit, acceptedText.length),
        };
    }
}
